import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Star } from "lucide-react";

/**
 * Admin-only toggle to pin/unpin a drop on the homepage featured rail.
 * Renders nothing unless the current user is on the ADMIN_EMAILS allowlist.
 */
export default function AdminFeatureToggle({ productId }) {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    api.get("/admin/me").then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/featured").then(({ data }) => {
      setFeatured(data.some((p) => p.id === productId));
    }).catch(() => {});
  }, [isAdmin, productId]);

  if (!isAdmin) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (featured) {
        await api.delete(`/admin/featured/${productId}`);
        setFeatured(false);
        toast.success("Removed from featured rail");
      } else {
        await api.post(`/admin/featured/${productId}`);
        setFeatured(true);
        toast.success("Pinned to featured rail");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update featured");
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      data-testid="admin-feature-toggle"
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-xs px-4 py-2.5 border ${
        featured
          ? "border-[#FF3366] bg-[#FF3366]/10 text-[#FF3366]"
          : "border-white/20 hover:border-white/50 text-white/80"
      } disabled:opacity-40`}
    >
      <Star className={`w-3.5 h-3.5 ${featured ? "fill-[#FF3366]" : ""}`} />
      {featured ? "Unfeature" : "Feature this drop"}
    </button>
  );
}
